import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';
import Navigation from './Navigation';
import Footer from './Footer';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Shield, 
  Clock, 
  User, 
  Edit, 
  Trash2, 
  Plus,
  AlertCircle,
  Filter,
  Eye,
  Download,
  Activity,
  Database,
  Search,
  Calendar
} from 'lucide-react';

const AuditTrail = () => {
  const { user, API } = useContext(UserContext);
  const [auditData, setAuditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [userFilter, setUserFilter] = useState('');
  const [showDetails, setShowDetails] = useState({});

  useEffect(() => {
    if (user && (user.role === 'owner' || user.role === 'system_admin')) {
      fetchAuditTrail();
    }
  }, [user, dateFilter, userFilter]);

  const fetchAuditTrail = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.append('user_role', user.role);
      params.append('user_id', user.id);
      if (dateFilter) params.append('date_filter', dateFilter);
      if (userFilter) params.append('user_filter', userFilter);
      
      const response = await axios.get(`${API}/system/audit-trail-secret?${params}`);
      setAuditData(response.data);
      
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      if (error.response?.status === 404) {
        toast.error('Accès refusé à cette fonctionnalité');
      } else {
        toast.error('Erreur lors du chargement de l\'audit');
      }
      setAuditData(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleDetails = (logId) => {
    setShowDetails(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const getActionColor = (actionType) => {
    switch (actionType) {
      case 'CREATE': return 'bg-green-100 text-green-800';
      case 'UPDATE': return 'bg-orange-100 text-orange-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'CREATE': return <Plus className="h-4 w-4" />;
      case 'UPDATE': return <Edit className="h-4 w-4" />;
      case 'DELETE': return <Trash2 className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getTableDisplayName = (tableName) => {
    const tableNames = {
      'production_records': 'Production',
      'sales_records': 'Ventes',
      'return_records': 'Retours',
      'stock_records': 'Stock',
      'expense_records': 'Dépenses',
      'supply_records': 'Approvisionnements',
      'users': 'Utilisateurs',
      'companies': 'Entreprises'
    };
    return tableNames[tableName] || tableName;
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'system_admin': return 'bg-red-100 text-red-800';
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      case 'staff': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const exportAuditData = () => {
    try {
      const csvData = auditData.audit_logs.map(log => ({
        'Date': new Date(log.timestamp).toLocaleDateString('fr-FR'),
        'Heure': new Date(log.timestamp).toLocaleTimeString('fr-FR'),
        'Utilisateur': log.user_username,
        'Rôle': log.user_role,
        'Action': log.action_type,
        'Table': getTableDisplayName(log.table_name),
        'ID Enregistrement': log.record_id
      }));

      const csvString = [
        Object.keys(csvData[0]).join(','),
        ...csvData.map(row => Object.values(row).map(val => `"${val}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-trail-${dateFilter || 'all'}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Export réalisé avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return 'Date invalide';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement du journal d'audit...</p>
          </div>
        </div>
      </div>
    );
  }

  // Vérification ultra-stricte des permissions
  if (!user || (user.role !== 'owner' && user.role !== 'system_admin')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Page Introuvable</h2>
            <p className="text-gray-600">Cette page n'existe pas.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Secret */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-r from-red-500 to-pink-600 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  🕵️ Journal d'Audit Système
                </h1>
                <p className="text-red-600 font-medium">
                  ⚠️ Fonctionnalité Confidentielle - Accès Ultra-Restreint
                </p>
              </div>
            </div>
            
            {auditData && (
              <Button
                onClick={exportAuditData}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="export-audit-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Exporter CSV
              </Button>
            )}
          </div>
        </div>

        {/* Filtres */}
        <Card className="mb-8 bg-white/80 backdrop-blur border-red-200 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <Filter className="h-5 w-5" />
              Filtres de Surveillance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Date de surveillance
                </Label>
                <Input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="h-10"
                  data-testid="date-filter"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Filtrer par utilisateur
                </Label>
                <Input
                  type="text"
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="h-10"
                  placeholder="Nom d'utilisateur..."
                  data-testid="user-filter"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Actions surveillées
                </Label>
                <div className="flex gap-2 flex-wrap">
                  <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
                    <Plus className="h-3 w-3" />
                    Créations
                  </Badge>
                  <Badge className="bg-orange-100 text-orange-800 flex items-center gap-1">
                    <Edit className="h-3 w-3" />
                    Modifications
                  </Badge>
                  <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
                    <Trash2 className="h-3 w-3" />
                    Suppressions
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Statistiques */}
        {auditData?.statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="bg-white/70 border-red-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Actions</p>
                    <p className="text-2xl font-bold text-red-600">{auditData.statistics.total_actions}</p>
                  </div>
                  <Activity className="h-8 w-8 text-red-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Créations</p>
                    <p className="text-2xl font-bold text-green-600">{auditData.statistics.actions_by_type.CREATE || 0}</p>
                  </div>
                  <Plus className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 border-orange-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Modifications</p>
                    <p className="text-2xl font-bold text-orange-600">{auditData.statistics.actions_by_type.UPDATE || 0}</p>
                  </div>
                  <Edit className="h-8 w-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 border-red-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Suppressions</p>
                    <p className="text-2xl font-bold text-red-600">{auditData.statistics.actions_by_type.DELETE || 0}</p>
                  </div>
                  <Trash2 className="h-8 w-8 text-red-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Journal d'Audit */}
        <Card className="bg-white/80 backdrop-blur border-red-200 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-red-700">
                <Database className="h-5 w-5" />
                Journal des Modifications
                {auditData?.audit_logs && (
                  <Badge className="bg-red-100 text-red-800 ml-2">
                    {auditData.audit_logs.length} enregistrement(s)
                  </Badge>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {auditData?.audit_logs?.length > 0 ? (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {auditData.audit_logs.map((log, index) => (
                  <div key={log.id || index} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge className={`${getActionColor(log.action_type)} flex items-center gap-1`}>
                            {getActionIcon(log.action_type)}
                            {log.action_type}
                          </Badge>
                          
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Database className="h-3 w-3" />
                            {getTableDisplayName(log.table_name)}
                          </Badge>
                          
                          <Badge className={`${getRoleColor(log.user_role)} flex items-center gap-1`}>
                            <User className="h-3 w-3" />
                            {log.user_username} ({log.user_role})
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                          <div>
                            <p><strong>🕒 Horodatage :</strong> {formatTimestamp(log.timestamp)}</p>
                            <p><strong>📋 Enregistrement :</strong> {log.record_id}</p>
                          </div>
                          <div>
                            <p><strong>👤 Utilisateur :</strong> {log.user_username}</p>
                            <p><strong>🛡️ Rôle :</strong> {log.user_role}</p>
                          </div>
                        </div>
                        
                        {/* Détails expandables */}
                        {(log.old_data || log.new_data) && (
                          <div className="mt-3">
                            <Button
                              onClick={() => toggleDetails(log.id)}
                              size="sm"
                              variant="outline"
                              className="text-xs"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              {showDetails[log.id] ? 'Masquer' : 'Voir'} Détails
                            </Button>
                            
                            {showDetails[log.id] && (
                              <div className="mt-3 p-3 bg-gray-50 rounded border">
                                {log.old_data && (
                                  <div className="mb-3">
                                    <p className="text-xs font-semibold text-red-600 mb-1">🔴 Avant modification :</p>
                                    <pre className="text-xs text-gray-700 bg-red-50 p-2 rounded overflow-auto">
                                      {JSON.stringify(log.old_data, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                
                                {log.new_data && (
                                  <div>
                                    <p className="text-xs font-semibold text-green-600 mb-1">🟢 Après modification :</p>
                                    <pre className="text-xs text-gray-700 bg-green-50 p-2 rounded overflow-auto">
                                      {JSON.stringify(log.new_data, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            {new Date(log.timestamp).toLocaleTimeString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune Action Enregistrée</h3>
                <p className="text-gray-600">
                  Aucune modification détectée pour les filtres sélectionnés.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Avertissement de Confidentialité */}
        <Card className="mt-8 bg-red-50 border-red-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-red-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-800 mb-2">⚠️ Avertissement de Confidentialité</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Cette page contient des informations confidentielles d'audit</li>
                  <li>• Toutes les modifications du système sont enregistrées</li>
                  <li>• L'accès à cette page est strictement restreint et surveillé</li>
                  <li>• Utilisez uniquement pour des fins de contrôle et sécurité</li>
                  <li>• Export recommandé pour archivage légal</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default AuditTrail;